import { apiRequest } from './axiosConfig';
import {
  TaapAccount,
  TaapBreakdown,
  TaapGranularity,
  TaapMetric,
  TaapPeriod,
  TaapPlatformFilter,
  TaapProduct,
  TaapProposal,
  TaapProposalList,
  TaapResponseScope,
  TaapScopeParams,
  TaapSettings,
  TaapSummary,
  TaapSyncStatus,
  TaapTimeseries,
} from '../../types/taap';

/**
 * バックエンドは全エンドポイントで { success, data } を返す。
 * apiRequest はボディをそのまま渡してくるので、ここで一度だけ剥がす。
 */
interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

const unwrap = <T>(envelope: ApiEnvelope<T>): T => envelope.data;

interface QueryParams extends TaapScopeParams {
  platform?: TaapPlatformFilter;
  period?: TaapPeriod;
}

/**
 * 画面で選んだ商品・アカウントを API のクエリに変換する。
 * 未選択（null）をそのまま送るとバックエンドの検証に掛かるのでキーごと落とす。
 */
export const toScopeParams = (productId: number | null, accountId: number | null): TaapScopeParams => ({
  ...(productId !== null ? { product_id: productId } : {}),
  ...(accountId !== null ? { ad_account_id: accountId } : {}),
});

class TaapService {
  async getSummary(params: QueryParams = {}): Promise<TaapSummary> {
    const response = await apiRequest.get<ApiEnvelope<TaapSummary>>('/taap/dashboard/summary', { params });
    return unwrap(response);
  }

  async getTimeseries(
    params: QueryParams & { metric?: TaapMetric; granularity?: TaapGranularity } = {},
  ): Promise<TaapTimeseries> {
    const response = await apiRequest.get<ApiEnvelope<TaapTimeseries>>('/taap/dashboard/timeseries', { params });
    return unwrap(response);
  }

  async getBreakdown(params: QueryParams = {}): Promise<TaapBreakdown> {
    const response = await apiRequest.get<ApiEnvelope<TaapBreakdown>>('/taap/dashboard/breakdown', { params });
    return unwrap(response);
  }

  /** 広告アカウントを紐付けた商品（name 昇順）。紐付けの無い商品は返らない。 */
  async getProducts(): Promise<TaapProduct[]> {
    const response = await apiRequest.get<ApiEnvelope<{ products: TaapProduct[] }>>('/taap/products');
    return unwrap(response).products;
  }

  /** 絞り込みセレクタの選択肢。接続状態に関わらず組織の全アカウントを返す。 */
  async getAccounts(): Promise<TaapAccount[]> {
    const response = await apiRequest.get<ApiEnvelope<{ accounts: TaapAccount[] }>>('/taap/accounts');
    return unwrap(response).accounts;
  }

  async getSyncStatus(): Promise<TaapSyncStatus> {
    const response = await apiRequest.get<ApiEnvelope<TaapSyncStatus>>('/taap/sync-status');
    return unwrap(response);
  }

  async getProposals(status: 'pending' | 'executed' | 'rejected' | 'all' = 'pending', scope: TaapScopeParams = {}): Promise<TaapProposalList> {
    const response = await apiRequest.get<ApiEnvelope<TaapProposalList>>('/taap/proposals', { params: { status, ...scope } });
    return unwrap(response);
  }

  async getProposal(id: string): Promise<TaapProposal> {
    const response = await apiRequest.get<ApiEnvelope<TaapProposal>>(`/taap/proposals/${id}`);
    return unwrap(response);
  }

  /** 「今すぐ分析」。毎朝の自動診断と同じ経路を手動で回す。 */
  async generateProposals(
    scope: TaapScopeParams = {},
  ): Promise<{ created_count: number; proposals: TaapProposal[]; scope?: TaapResponseScope }> {
    const response = await apiRequest.post<ApiEnvelope<{ created_count: number; proposals: TaapProposal[]; scope?: TaapResponseScope }>>(
      '/taap/proposals/generate',
      scope,
    );
    return unwrap(response);
  }

  async approveProposal(id: string): Promise<TaapProposal> {
    const response = await apiRequest.post<ApiEnvelope<TaapProposal>>(`/taap/proposals/${id}/approve`);
    return unwrap(response);
  }

  async rejectProposal(id: string, reason?: string): Promise<TaapProposal> {
    const response = await apiRequest.post<ApiEnvelope<TaapProposal>>(`/taap/proposals/${id}/reject`, { reason });
    return unwrap(response);
  }

  async getSettings(): Promise<TaapSettings> {
    const response = await apiRequest.get<ApiEnvelope<TaapSettings>>('/taap/settings');
    return unwrap(response);
  }

  async updateSettings(settings: Partial<TaapSettings>): Promise<TaapSettings> {
    const response = await apiRequest.put<ApiEnvelope<TaapSettings>>('/taap/settings', settings);
    return unwrap(response);
  }
}

export default new TaapService();
